/**
 * RuleSuggestionsPage - Verwaltung von Regelvorschlägen und aktiven Regeln
 * 
 * Zeigt Vorschläge basierend auf wiederholten Entscheidungen und ermöglicht
 * deren Annahme, Anpassung oder Ablehnung.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Lightbulb, Check, X, Clock, Settings, Play, 
  ChevronDown, ChevronUp, AlertCircle, Trash2,
  ToggleLeft, ToggleRight, Edit, TestTube, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RuleSuggestionsPage = () => {
  // State
  const [activeTab, setActiveTab] = useState('pending');
  const [suggestions, setSuggestions] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [status, setStatus] = useState(null);
  
  // Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSuggestion, setEditingSuggestion] = useState(null);
  const [editForm, setEditForm] = useState({
    useSubjectContains: false,
    subjectContains: '',
    hasAttachments: null,
    action: 'archive',
    visibility: 'admin_only',
    templateId: null
  });
  
  // Test Modal State
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testForm, setTestForm] = useState({
    fromAddress: '',
    subject: '',
    hasAttachments: false
  });
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  
  // Snooze Dropdown
  const [snoozeDropdownId, setSnoozeDropdownId] = useState(null);

  const getToken = () => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        return parsed.token;
      } catch {
        return null;
      }
    }
    return null;
  };
  const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  // Daten laden
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const authHeader = getAuthHeader();
      const [suggestionsRes, rulesRes, statusRes] = await Promise.all([
        axios.get(`${API}/mail/rule-suggestions?status=${activeTab}`, authHeader),
        axios.get(`${API}/mail/inbox-rules`, authHeader),
        axios.get(`${API}/mail/rule-suggestions/status`, authHeader)
      ]);
      
      setSuggestions(suggestionsRes.data.suggestions || []);
      setRules(rulesRes.data.rules || []);
      setStatus(statusRes.data);
    } catch (err) {
      console.error('Load error:', err);
      setError(err.response?.data?.detail || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Toast schließen nach 3s
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Vorschlag annehmen
  const handleAccept = async (suggestionId) => {
    try {
      const res = await axios.post(
        `${API}/mail/rule-suggestions/${suggestionId}/accept`,
        {},
        getAuthHeader()
      );
      
      if (res.data.success) {
        setSuccess(`Regel "${res.data.rule_name}" erstellt`);
        loadData();
      } else {
        setError(res.data.error || 'Fehler beim Annehmen');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Annehmen');
    }
  };

  // Vorschlag ablehnen
  const handleDismiss = async (suggestionId) => {
    try {
      const res = await axios.post(
        `${API}/mail/rule-suggestions/${suggestionId}/dismiss`,
        {},
        getAuthHeader()
      );
      
      if (res.data.success) {
        setSuccess('Vorschlag abgelehnt');
        loadData();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Ablehnen');
    }
  };

  // Vorschlag snoozen
  const handleSnooze = async (suggestionId, days = 7) => {
    try {
      const res = await axios.post(
        `${API}/mail/rule-suggestions/${suggestionId}/snooze?days=${days}`,
        {},
        getAuthHeader()
      );
      
      if (res.data.success) {
        setSuccess(`Vorschlag für ${days} Tage zurückgestellt`);
        setSnoozeDropdownId(null);
        loadData();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Snoozen');
    }
  };

  // Edit Modal öffnen
  const openEditModal = (suggestion) => {
    setEditingSuggestion(suggestion);
    setEditForm({
      useSubjectContains: false,
      subjectContains: suggestion.subject_norm || '',
      hasAttachments: suggestion.has_attachments,
      action: suggestion.suggested_actions?.action || 'archive',
      visibility: suggestion.suggested_actions?.visibility || 'admin_only',
      templateId: suggestion.suggested_actions?.template_id || null
    });
    setEditModalOpen(true);
  };

  // Mit Anpassungen annehmen
  const handleAcceptWithEdits = async () => {
    if (!editingSuggestion) return;
    
    try {
      const payload = {
        action: editForm.action,
        visibility: editForm.visibility,
        template_id: editForm.templateId
      };
      
      if (editForm.useSubjectContains && editForm.subjectContains) {
        payload.subject_contains = editForm.subjectContains;
      }
      
      if (editForm.hasAttachments !== null) {
        payload.has_attachments = editForm.hasAttachments;
      }
      
      const res = await axios.post(
        `${API}/mail/rule-suggestions/${editingSuggestion.id}/accept-with-edits`,
        payload,
        getAuthHeader()
      );
      
      if (res.data.success) {
        setSuccess(`Angepasste Regel "${res.data.rule_name}" erstellt`);
        setEditModalOpen(false);
        setEditingSuggestion(null);
        loadData();
      } else {
        setError(res.data.error || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  // Regel testen
  const handleTest = async () => {
    setTestLoading(true);
    setTestResult(null);
    
    try {
      const params = new URLSearchParams({
        from_address: testForm.fromAddress,
        subject: testForm.subject,
        has_attachments: testForm.hasAttachments
      });
      
      const res = await axios.post(
        `${API}/mail/rule-suggestions/test/simulate-mail?${params}`,
        {},
        getAuthHeader()
      );
      
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.detail || 'Test fehlgeschlagen' });
    } finally {
      setTestLoading(false);
    }
  };

  // Rebuild/Backfill State
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);

  // Rebuild/Backfill ausführen
  const handleRebuild = async (days = 30) => {
    setRebuildLoading(true);
    setRebuildResult(null);
    setError(null);
    
    try {
      const res = await axios.post(
        `${API}/mail/rule-suggestions/rebuild?days=${days}`,
        {},
        getAuthHeader()
      );
      
      if (res.data.success) {
        setRebuildResult(res.data);
        setSuccess(`Rebuild abgeschlossen: ${res.data.events_created} Events erstellt, ${res.data.suggestions_pending} Vorschläge pending`);
        loadData(); // Daten neu laden
      } else {
        setError(res.data.error || 'Rebuild fehlgeschlagen');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Rebuild fehlgeschlagen');
    } finally {
      setRebuildLoading(false);
    }
  };

  // Regel aktivieren/deaktivieren
  const toggleRule = async (ruleId, enabled) => {
    try {
      // TODO: Implement rule toggle endpoint if needed
      setSuccess(`Regel ${enabled ? 'aktiviert' : 'deaktiviert'}`);
      loadData();
    } catch (err) {
      setError('Fehler beim Ändern der Regel');
    }
  };

  // Tab Counts
  const tabCounts = {
    pending: status?.suggestion_counts?.pending || 0,
    snoozed: status?.suggestion_counts?.snoozed || 0,
    accepted: status?.suggestion_counts?.accepted || 0,
    dismissed: status?.suggestion_counts?.dismissed || 0
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900" data-testid="rule-suggestions-page">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Lightbulb className="h-6 w-6 text-yellow-500" />
                Regelvorschläge
              </h1>
              <p className="text-gray-500 dark:text-gray-400 mt-1">
                Basierend auf Ihren Entscheidungen schlägt das System automatische Regeln vor.
              </p>
            </div>
            
            {/* Rebuild Button */}
            <button
              onClick={() => handleRebuild(30)}
              disabled={rebuildLoading}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="rebuild-btn"
              title="Analysiert historische Entscheidungen und erstellt Vorschläge"
            >
              {rebuildLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Berechne...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Neu berechnen (30 Tage)
                </>
              )}
            </button>
          </div>
          
          {/* Rebuild Result */}
          {rebuildResult && (
            <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm">
              <div className="font-medium text-orange-800 mb-1">Rebuild-Ergebnis:</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-orange-700">
                <div>Mails verarbeitet: <strong>{rebuildResult.messages_processed}</strong></div>
                <div>Events erstellt: <strong>{rebuildResult.events_created}</strong></div>
                <div>Events übersprungen: <strong>{rebuildResult.events_skipped_existing}</strong></div>
                <div>Vorschläge pending: <strong>{rebuildResult.suggestions_pending}</strong></div>
              </div>
            </div>
          )}
          
          {/* Status Info */}
          {status && (
            <div className="mt-3 flex gap-4 text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Aktive Regeln: <strong>{status.active_rules_count}</strong>
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                Auto-Delete: <strong className={status.auto_delete_enabled ? 'text-red-500' : 'text-green-500'}>
                  {status.auto_delete_enabled ? 'Aktiviert' : 'Deaktiviert (→ Archiv)'}
                </strong>
              </span>
            </div>
          )}
          
          {/* Suggestions Summary */}
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex flex-wrap gap-4 text-sm">
              <span className={`font-medium ${tabCounts.pending > 0 ? 'text-yellow-600' : 'text-gray-500'}`}>
                Pending: <strong>{tabCounts.pending || 0}</strong>
              </span>
              <span className="text-orange-600">
                Snoozed: <strong>{tabCounts.snoozed || 0}</strong>
              </span>
              <span className="text-green-600">
                Accepted: <strong>{tabCounts.accepted || 0}</strong>
              </span>
              <span className="text-gray-500">
                Dismissed: <strong>{tabCounts.dismissed || 0}</strong>
              </span>
            </div>
            {tabCounts.pending === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Keine offenen Vorschläge. Neue Vorschläge werden automatisch erstellt, wenn ein Absender ≥6 Mails mit gleichem Muster erhält.
              </p>
            )}
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded-lg flex items-center gap-2 text-green-700">
            <Check className="h-5 w-5" />
            {success}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
          {[
            { key: 'pending', label: 'Ausstehend', icon: Clock },
            { key: 'snoozed', label: 'Zurückgestellt', icon: Clock },
            { key: 'accepted', label: 'Angenommen', icon: Check },
            { key: 'dismissed', label: 'Abgelehnt', icon: X }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tabCounts[tab.key] > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  tab.key === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {tabCounts[tab.key]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-2 text-gray-500">Laden...</p>
          </div>
        )}

        {/* Suggestions List */}
        {!loading && suggestions.length === 0 && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <Lightbulb className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600" />
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              {activeTab === 'pending' 
                ? 'Keine ausstehenden Vorschläge. Treffen Sie mehr Entscheidungen!'
                : `Keine ${activeTab === 'snoozed' ? 'zurückgestellten' : activeTab === 'accepted' ? 'angenommenen' : 'abgelehnten'} Vorschläge.`
              }
            </p>
          </div>
        )}

        {!loading && suggestions.length > 0 && (
          <div className="space-y-4">
            {suggestions.map(suggestion => (
              <SuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                activeTab={activeTab}
                onAccept={() => handleAccept(suggestion.id)}
                onDismiss={() => handleDismiss(suggestion.id)}
                onSnooze={(days) => handleSnooze(suggestion.id, days)}
                onEdit={() => openEditModal(suggestion)}
                snoozeDropdownId={snoozeDropdownId}
                setSnoozeDropdownId={setSnoozeDropdownId}
              />
            ))}
          </div>
        )}

        {/* Aktive Regeln Sektion */}
        <div className="mt-10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
            <Settings className="h-5 w-5" />
            Aktive Regeln
            <span className="text-sm font-normal text-gray-500">
              (Priorität: kleinere Zahl = höher)
            </span>
          </h2>
          
          {rules.length === 0 ? (
            <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-gray-500">Noch keine aktiven Regeln vorhanden.</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Regel</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Bedingungen</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Aktion</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Priorität</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                  {rules.map(rule => (
                    <tr key={rule.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{rule.name}</div>
                        <div className="text-xs text-gray-500">{rule.conditions?.from_address}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {rule.conditions?.subject_norm && (
                          <div>Betreff: <code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">{rule.conditions.subject_norm}</code></div>
                        )}
                        {rule.conditions?.subject_contains && (
                          <div>Enthält: <code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">{rule.conditions.subject_contains}</code></div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          rule.actions?.auto_action === 'delete' ? 'bg-red-100 text-red-700' :
                          rule.actions?.auto_action === 'archive' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {rule.actions?.auto_action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-mono">{rule.priority}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          rule.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {rule.enabled ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Test Button */}
          <button
            onClick={() => setTestModalOpen(true)}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            data-testid="test-rule-btn"
          >
            <TestTube className="h-4 w-4" />
            Regel testen
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {editModalOpen && editingSuggestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4" data-testid="edit-modal">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Regel anpassen</h3>
            </div>
            
            <div className="p-4 space-y-4">
              {/* From Address (read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Absender (fix)
                </label>
                <input
                  type="text"
                  value={editingSuggestion.from_address}
                  readOnly
                  className="w-full px-3 py-2 border rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                />
              </div>
              
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Betreff-Muster
                </label>
                <input
                  type="text"
                  value={editingSuggestion.subject_norm}
                  readOnly
                  className="w-full px-3 py-2 border rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 mb-2"
                />
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.useSubjectContains}
                    onChange={(e) => setEditForm({...editForm, useSubjectContains: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Stattdessen "enthält" verwenden
                  </span>
                </label>
                
                {editForm.useSubjectContains && (
                  <input
                    type="text"
                    value={editForm.subjectContains}
                    onChange={(e) => setEditForm({...editForm, subjectContains: e.target.value})}
                    placeholder="z.B. newsletter"
                    className="mt-2 w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  />
                )}
              </div>
              
              {/* Has Attachments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Anhänge-Filter
                </label>
                <select
                  value={editForm.hasAttachments === null ? 'any' : editForm.hasAttachments ? 'yes' : 'no'}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    hasAttachments: e.target.value === 'any' ? null : e.target.value === 'yes'
                  })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                >
                  <option value="any">Egal</option>
                  <option value="yes">Mit Anhängen</option>
                  <option value="no">Ohne Anhänge</option>
                </select>
              </div>
              
              {/* Action */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Aktion
                </label>
                <select
                  value={editForm.action}
                  onChange={(e) => setEditForm({...editForm, action: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                >
                  <option value="archive">Archivieren</option>
                  <option value="delete">Löschen</option>
                  <option value="ignore">Ignorieren</option>
                </select>
              </div>
              
              {/* Visibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sichtbarkeit
                </label>
                <select
                  value={editForm.visibility}
                  onChange={(e) => setEditForm({...editForm, visibility: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                >
                  <option value="admin_only">Nur Admin</option>
                  <option value="admin_employee">Admin + Mitarbeiter</option>
                </select>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setEditModalOpen(false);
                  setEditingSuggestion(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 rounded-lg"
              >
                Abbrechen
              </button>
              <button
                onClick={handleAcceptWithEdits}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                data-testid="save-edit-btn"
              >
                Angepasste Regel erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Modal */}
      {testModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4" data-testid="test-modal">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Regel testen</h3>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Absender (from_address)
                </label>
                <input
                  type="text"
                  value={testForm.fromAddress}
                  onChange={(e) => setTestForm({...testForm, fromAddress: e.target.value})}
                  placeholder="z.B. linkedin@linkedin.com"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Betreff
                </label>
                <input
                  type="text"
                  value={testForm.subject}
                  onChange={(e) => setTestForm({...testForm, subject: e.target.value})}
                  placeholder="z.B. LinkedIn: 5 neue Benachrichtigungen"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={testForm.hasAttachments}
                  onChange={(e) => setTestForm({...testForm, hasAttachments: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">Hat Anhänge</span>
              </label>
              
              {/* Test Result */}
              {testResult && (
                <div className={`p-3 rounded-lg ${
                  testResult.rule_applied 
                    ? 'bg-green-100 border border-green-300 text-green-800' 
                    : 'bg-yellow-100 border border-yellow-300 text-yellow-800'
                }`}>
                  {testResult.rule_applied ? (
                    <>
                      <div className="font-medium">✅ Regel greift!</div>
                      <div className="text-sm mt-1">
                        <div>Regel: {testResult.rule_result?.rule_name}</div>
                        <div>Aktion: {testResult.rule_result?.action_applied}</div>
                        {testResult.rule_result?.converted_from_delete && (
                          <div className="text-orange-600">⚠️ Delete → Archive (Global Toggle off)</div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="font-medium">❌ Keine passende Regel gefunden</div>
                  )}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setTestModalOpen(false);
                  setTestResult(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 rounded-lg"
              >
                Schließen
              </button>
              <button
                onClick={handleTest}
                disabled={testLoading || !testForm.fromAddress || !testForm.subject}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {testLoading ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Teste...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Testen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Suggestion Card Component
const SuggestionCard = ({ 
  suggestion, 
  activeTab,
  onAccept, 
  onDismiss, 
  onSnooze, 
  onEdit,
  snoozeDropdownId,
  setSnoozeDropdownId
}) => {
  const [expanded, setExpanded] = useState(false);
  
  const actions = suggestion.suggested_actions || {};
  const stats = suggestion.stats || {};
  
  return (
    <div 
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
      data-testid={`suggestion-${suggestion.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-white">
              {suggestion.from_address}
            </span>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              suggestion.confidence_score >= 90 ? 'bg-green-100 text-green-700' :
              suggestion.confidence_score >= 70 ? 'bg-yellow-100 text-yellow-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {suggestion.confidence_score}%
            </span>
          </div>
          
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
              {suggestion.subject_norm}
            </code>
          </div>
          
          <div className="flex gap-4 mt-2 text-xs text-gray-500">
            <span>Aktion: <strong className={actions.action === 'delete' ? 'text-red-600' : 'text-blue-600'}>
              {actions.action}
            </strong></span>
            <span>Sichtbarkeit: <strong>{actions.visibility === 'admin_only' ? 'Nur Admin' : 'Admin+MA'}</strong></span>
            <span>Treffer (30T): <strong>{stats.count_last_30d}</strong></span>
            <span>Gesamt: <strong>{stats.count_total}</strong></span>
          </div>
        </div>
        
        {/* Actions - nur für pending */}
        {activeTab === 'pending' && (
          <div className="flex gap-2 ml-4">
            <button
              onClick={onAccept}
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center gap-1"
              data-testid="accept-btn"
            >
              <Check className="h-4 w-4" />
              Annehmen
            </button>
            
            <button
              onClick={onEdit}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-1"
              data-testid="edit-btn"
            >
              <Edit className="h-4 w-4" />
              Anpassen
            </button>
            
            <button
              onClick={onDismiss}
              className="px-3 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm flex items-center gap-1"
              data-testid="dismiss-btn"
            >
              <X className="h-4 w-4" />
              Ablehnen
            </button>
            
            {/* Snooze Dropdown */}
            <div className="relative">
              <button
                onClick={() => setSnoozeDropdownId(snoozeDropdownId === suggestion.id ? null : suggestion.id)}
                className="px-3 py-1.5 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-sm flex items-center gap-1"
                data-testid="snooze-btn"
              >
                <Clock className="h-4 w-4" />
                Später
                <ChevronDown className="h-3 w-3" />
              </button>
              
              {snoozeDropdownId === suggestion.id && (
                <div className="absolute right-0 mt-1 w-32 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 z-10">
                  {[7, 14, 30].map(days => (
                    <button
                      key={days}
                      onClick={() => onSnooze(days)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600"
                    >
                      {days} Tage
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Examples Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-3 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
      >
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {expanded ? 'Beispiele ausblenden' : `${suggestion.examples?.length || 0} Beispiele anzeigen`}
      </button>
      
      {/* Examples */}
      {expanded && suggestion.examples && suggestion.examples.length > 0 && (
        <div className="mt-3 pl-4 border-l-2 border-gray-200 dark:border-gray-600 space-y-2">
          {suggestion.examples.slice(0, 3).map((ex, idx) => (
            <div key={idx} className="text-sm">
              <span className="text-gray-500">{ex.date?.substring(0, 10)}:</span>
              <span className="ml-2 text-gray-700 dark:text-gray-300">{ex.subject}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RuleSuggestionsPage;
